import CloseIcon from "@mui/icons-material/CloseRounded";
import { Autocomplete, IconButton, TableRow, TextField } from "@mui/material";
import NumberTextField from "@entrilia/shared/components/inputs/NumberTextField";
import useDefinedContext from "@entrilia/shared/hooks/useDefinedContext";
import type { ObjectFieldValues } from "../../../../../../api/types/objectTypes";
import ObjectAutocomplete from "./ObjectAutocomplete";
import TransactionEditContext from "./TransactionEditContext";
import type { TransactionLine } from "./transactionEditState";
import { removeTransactionLineAction } from "./transactionEditState";
import TransactionTableCell from "./TransactionTableCell";

const noOutlineSx = {
  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
};

interface Props {
  line: TransactionLine;
  allTransactionCodes: ObjectFieldValues[];
  allPositionTypes: ObjectFieldValues[];
  allAccounts: ObjectFieldValues[];
  allEntityAllocationRules: ObjectFieldValues[];
  allInvestorAllocationRules: ObjectFieldValues[];
  currencyCodes: string[];
}

const TransactionLineRow = ({
  line,
  allTransactionCodes,
  allPositionTypes,
  allAccounts,
  allEntityAllocationRules,
  allInvestorAllocationRules,
  currencyCodes,
}: Props) => {
  const { editState, setEditState } = useDefinedContext(TransactionEditContext);

  const handleRowChange = (update: Partial<TransactionLine>) => {
    setEditState((state) => {
      const lines = state.lines.map((tl) => (tl.changeSetId === line.changeSetId ? { ...tl, ...update } : tl));
      return { ...state, lines };
    });
  };

  const handleRemoveLine = () => {
    setEditState(removeTransactionLineAction(line.changeSetId));
  };

  const invalidFields = editState.validationResults?.linesInvalidFields[line.changeSetId] ?? [];

  return (
    <TableRow>
      <TransactionTableCell hasError={invalidFields.includes("transactionCode")}>
        <ObjectAutocomplete
          options={allTransactionCodes}
          value={line.transactionCode}
          onChange={(transactionCode) => handleRowChange({ transactionCode })}
          sx={noOutlineSx}
        />
      </TransactionTableCell>

      <TransactionTableCell hasError={invalidFields.includes("positionType")}>
        <ObjectAutocomplete
          options={allPositionTypes}
          value={line.positionType}
          onChange={(positionType) => handleRowChange({ positionType })}
          sx={noOutlineSx}
        />
      </TransactionTableCell>

      <TransactionTableCell hasError={invalidFields.includes("positionNo")}>
        <TextField
          fullWidth
          value={line.positionNo}
          onChange={(e) => handleRowChange({ positionNo: e.target.value })}
          sx={noOutlineSx}
        />
      </TransactionTableCell>

      <TransactionTableCell hasError={invalidFields.includes("account")}>
        <ObjectAutocomplete
          options={allAccounts}
          value={line.account}
          onChange={(account) => handleRowChange({ account })}
          sx={noOutlineSx}
        />
      </TransactionTableCell>

      <TransactionTableCell>
        <TextField
          fullWidth
          value={line.description}
          onChange={(e) => handleRowChange({ description: e.target.value })}
          sx={noOutlineSx}
        />
      </TransactionTableCell>

      <TransactionTableCell hasError={invalidFields.includes("shares")}>
        <NumberTextField
          fullWidth
          numberValue={line.shares}
          onNumberChange={(shares) => handleRowChange({ shares })}
          sx={noOutlineSx}
        />
      </TransactionTableCell>

      <TransactionTableCell hasError={invalidFields.includes("amount")}>
        <NumberTextField
          fullWidth
          numberValue={line.amount}
          onNumberChange={(amount) => handleRowChange({ amount: amount ?? 0 })}
          sx={noOutlineSx}
        />
      </TransactionTableCell>

      <TransactionTableCell hasError={invalidFields.includes("currencyCode")}>
        <Autocomplete
          fullWidth
          openOnFocus
          disableClearable={line.currencyCode !== null}
          options={currencyCodes}
          value={line.currencyCode}
          onChange={(_, currencyCode) => handleRowChange({ currencyCode })}
          renderInput={(params) => <TextField {...params} />}
          sx={noOutlineSx}
        />
      </TransactionTableCell>

      {editState.isMultiEntity && (
        <TransactionTableCell hasError={invalidFields.includes("entityAllocationRule")}>
          <ObjectAutocomplete
            options={allEntityAllocationRules}
            value={line.entityAllocationRule}
            onChange={(entityAllocationRule) => handleRowChange({ entityAllocationRule })}
            sx={noOutlineSx}
          />
        </TransactionTableCell>
      )}

      {!editState.isMultiEntity && (
        <TransactionTableCell hasError={invalidFields.includes("investorAllocationRule")}>
          <ObjectAutocomplete
            options={allInvestorAllocationRules}
            value={line.investorAllocationRule}
            onChange={(investorAllocationRule) => handleRowChange({ investorAllocationRule })}
            sx={noOutlineSx}
          />
        </TransactionTableCell>
      )}

      <TransactionTableCell width={60}>
        <IconButton color="error" onClick={handleRemoveLine}>
          <CloseIcon />
        </IconButton>
      </TransactionTableCell>
    </TableRow>
  );
};

export default TransactionLineRow;
